/**
 * @boli/core - Time Synchronization Utility
 *
 * Platform-agnostic clock drift calculation for accurate auction countdowns.
 * Critical for fair bidding when video stream has 2-5 second latency.
 *
 * Algorithm: RTT (Round-Trip Time) based offset calculation
 * Formula: Drift = (T_response - T_request) / 2
 * True Server Time = Local Time + Offset
 */

import type { TimeSyncResult, TimeSyncResponse } from '@boli/types';

// ============================================================================
// CONFIGURATION
// ============================================================================

export interface TimeSyncConfig {
  syncIntervalMs: number; // Auto-sync frequency
  maxRttMs: number; // Reject syncs with RTT higher than this
  minSamples: number; // Minimum samples for reliable offset
  maxSamples: number; // Maximum samples to average
}

const DEFAULT_CONFIG: TimeSyncConfig = {
  syncIntervalMs: 30000, // Re-sync every 30 seconds
  maxRttMs: 5000, // Reject syncs with RTT > 5s
  minSamples: 3, // Need at least 3 samples
  maxSamples: 10, // Average last 10 samples
};

// ============================================================================
// TIME SYNC SERVICE
// ============================================================================

export class TimeSyncService {
  private config: TimeSyncConfig;
  private offset: number = 0;
  private samples: TimeSyncResult[] = [];
  private lastSyncTime: number = 0;
  private syncIntervalId?: ReturnType<typeof setInterval>;
  private isInitialized: boolean = false;
  private fetchAdapter?: (url: string, options?: RequestInit) => Promise<Response>;

  constructor(config: Partial<TimeSyncConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Initialize time synchronization with auto-sync
   */
  async initialize(
    syncEndpoint: string,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>
  ): Promise<void> {
    this.fetchAdapter = fetchAdapter;

    // Perform initial sync with multiple samples
    for (let i = 0; i < this.config.minSamples; i++) {
      try {
        await this.performSync(syncEndpoint);
        // Small delay between samples
        await new Promise((r) => setTimeout(r, 100));
      } catch (error) {
        console.warn(`Time sync sample ${i + 1} failed:`, error);
      }
    }

    this.isInitialized = true;

    // Start auto-sync
    this.syncIntervalId = setInterval(() => {
      this.performSync(syncEndpoint).catch(console.error);
    }, this.config.syncIntervalMs);
  }

  /**
   * Perform a single time sync with the server
   */
  async performSync(endpoint: string): Promise<TimeSyncResult> {
    if (!this.fetchAdapter) {
      throw new Error('TimeSyncService not initialized with fetch adapter');
    }

    const t0 = Date.now(); // Client request time

    try {
      const response = await this.fetchAdapter(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      });

      const t3 = Date.now(); // Client response time
      const data: TimeSyncResponse = await response.json();
      const t1 = data.serverTime; // Server time

      // Calculate RTT (Round-Trip Time)
      const rtt = t3 - t0;

      // Reject if RTT is too high (network too slow)
      if (rtt > this.config.maxRttMs) {
        console.warn(`Time sync rejected: RTT too high (${rtt}ms)`);
        throw new Error(`RTT_TOO_HIGH: ${rtt}ms`);
      }

      // Calculate one-way latency (half of RTT)
      const oneWayLatency = rtt / 2;

      // Estimate server time at the moment we received the response
      const estimatedServerTime = t1 + oneWayLatency;

      // Calculate clock offset
      const sampleOffset = estimatedServerTime - t3;

      const result: TimeSyncResult = {
        serverTime: t1,
        clientTime: t0,
        offset: sampleOffset,
        rtt,
      };

      // Add to samples
      this.addSample(result);

      // Update last sync time
      this.lastSyncTime = Date.now();

      return result;
    } catch (error) {
      console.error('Time sync failed:', error);
      throw error;
    }
  }

  /**
   * Add a sync sample and recalculate offset
   */
  private addSample(result: TimeSyncResult): void {
    this.samples.push(result);

    // Keep only the most recent samples
    if (this.samples.length > this.config.maxSamples) {
      this.samples = this.samples.slice(-this.config.maxSamples);
    }

    // Calculate weighted average offset
    // Give more weight to recent samples and lower RTT samples
    this.recalculateOffset();
  }

  /**
   * Recalculate offset using weighted average
   */
  private recalculateOffset(): void {
    if (this.samples.length === 0) {
      return;
    }

    // Sort by RTT (prefer lower RTT samples)
    const sortedSamples = [...this.samples].sort((a, b) => a.rtt - b.rtt);

    // Take the best half of samples
    const bestSamples = sortedSamples.slice(0, Math.ceil(sortedSamples.length / 2));

    // Calculate median offset from best samples
    const offsets = bestSamples.map((s) => s.offset).sort((a, b) => a - b);
    const midIndex = Math.floor(offsets.length / 2);

    if (offsets.length % 2 === 0) {
      this.offset = (offsets[midIndex - 1] + offsets[midIndex]) / 2;
    } else {
      this.offset = offsets[midIndex];
    }
  }

  /**
   * Get the current server time
   */
  getServerTime(): number {
    return Date.now() + this.offset;
  }

  /**
   * Get the offset between server and client clocks
   */
  getOffset(): number {
    return this.offset;
  }

  /**
   * Get time since last sync
   */
  getTimeSinceLastSync(): number {
    return Date.now() - this.lastSyncTime;
  }

  /**
   * Check if sync is stale (needs refresh)
   */
  isSyncStale(maxAgeMs: number = 60000): boolean {
    return this.getTimeSinceLastSync() > maxAgeMs;
  }

  /**
   * Check if service is initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.samples.length >= this.config.minSamples;
  }

  /**
   * Convert server timestamp to local time
   */
  serverToLocal(serverTimestamp: number): number {
    return serverTimestamp - this.offset;
  }

  /**
   * Convert local timestamp to server time
   */
  localToServer(localTimestamp: number): number {
    return localTimestamp + this.offset;
  }

  /**
   * Get time remaining until a server timestamp
   */
  getTimeRemaining(serverTimestamp: number): number {
    const currentServerTime = this.getServerTime();
    return Math.max(0, serverTimestamp - currentServerTime);
  }

  /**
   * Format time remaining as MM:SS
   */
  formatTimeRemaining(milliseconds: number): string {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Format time remaining with more detail
   */
  formatTimeRemainingDetailed(milliseconds: number): {
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
    formatted: string;
  } {
    const hours = Math.floor(milliseconds / 3600000);
    const minutes = Math.floor((milliseconds % 3600000) / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    const ms = milliseconds % 1000;

    let formatted: string;
    if (hours > 0) {
      formatted = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    } else {
      formatted = `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    }

    return {
      hours,
      minutes,
      seconds,
      milliseconds: ms,
      formatted,
    };
  }

  /**
   * Get sync quality (0-100)
   */
  getSyncQuality(): number {
    if (this.samples.length === 0) return 0;

    const avgRtt =
      this.samples.reduce((sum, s) => sum + s.rtt, 0) / this.samples.length;

    // Quality based on RTT (lower is better)
    // 100ms RTT = 100 quality, 5000ms RTT = 0 quality
    const quality = Math.max(0, 100 - (avgRtt / 50));

    return Math.round(quality);
  }

  /**
   * Get average RTT
   */
  getAverageRtt(): number {
    if (this.samples.length === 0) return 0;
    return this.samples.reduce((sum, s) => sum + s.rtt, 0) / this.samples.length;
  }

  /**
   * Cleanup and stop auto-sync
   */
  destroy(): void {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = undefined;
    }
    this.samples = [];
    this.isInitialized = false;
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

let timeSyncInstance: TimeSyncService | null = null;

export function getTimeSyncService(config?: Partial<TimeSyncConfig>): TimeSyncService {
  if (!timeSyncInstance) {
    timeSyncInstance = new TimeSyncService(config);
  }
  return timeSyncInstance;
}

export function destroyTimeSyncService(): void {
  if (timeSyncInstance) {
    timeSyncInstance.destroy();
    timeSyncInstance = null;
  }
}
